import React, { cloneElement, forwardRef } from 'react';
import { styled, FormControl, InputLabel, Select } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { useViewport } from 'contexts/viewport';

const CustomFormControl = styled(FormControl)(({ renderValue }) => {
    return {
        width: renderValue ? 'auto' : '100%',
    };
});

const CustomInputLabel = styled(InputLabel)(({ $disabled, theme }) => {
    return {
        position: 'unset',
        marginBottom: theme.spacing(1),
        color: theme.palette.ensightful.neutral[900],
        transform: 'none',
        whiteSpace: 'initial',
        wordWrap: 'break-word',
        '&.Mui-focused': {
            color: 'initial',
        },
        '& .MuiInputLabel-asterisk': {
            color: theme.palette.ensightful.error[600],
            ...($disabled && {
                color: theme.palette.ensightful.neutral[400],
            }),
        },
        ...($disabled && {
            color: `${theme.palette.ensightful.neutral[400]} !important`,
        }),
    };
});

const CustomSelect = styled(Select)(({ $disabled, error, fontStyles, renderValue, $size, theme }) => {
    const height = {
        small: '28px',
        medium: '36px',
        large: '48px',
    }[$size];

    const padding = {
        small: '6px 12px',
        medium: '10px 12px',
        large: '14px 12px',
    }[$size];

    return {
        height,
        width: renderValue ? 'auto' : '100%',
        background: theme.palette.ensightful.shades[0],
        '&:focus-within': {
            outline: `${theme.palette.ensightful.secondary[100]} solid 3px`,
        },
        '&:hover': {
            '& > fieldset': {
                borderColor: `${theme.palette.ensightful.primary[700]} !important`,
                ...(error && {
                    borderColor: `${theme.palette.ensightful.error[700]} !important`,
                }),
            },
        },
        '&.Mui-focused': {
            '& > fieldset': {
                border: `1px solid ${theme.palette.ensightful.primary[500]} !important`,
                ...($disabled && {
                    border: `1px solid ${theme.palette.ensightful.neutral[400]} !important`,
                }),
                ...(error && {
                    border: `1px solid ${theme.palette.ensightful.error[700]} !important`,
                }),
            },
        },
        ...($disabled && {
            borderColor: theme.palette.ensightful.neutral[400],
            background: theme.palette.ensightful.neutral[300],
            color: theme.palette.ensightful.neutral[500],
            pointerEvents: 'none',
        }),
        ...(error && {
            borderColor: `${theme.palette.ensightful.error[700]} !important`,
        }),
        '& > div': {
            padding,
            ...fontStyles,
        },
        '& > svg': {
            fontSize: '1.5rem',
            ...($disabled && {
                color: theme.palette.ensightful.neutral[500],
            }),
        },
        '& > fieldset': {
            border: renderValue ? 'none' : `1px solid ${theme.palette.ensightful.neutral[500]}`,
            transition: theme.transitions.default,
            ...($disabled && {
                borderColor: theme.palette.ensightful.neutral[400],
            }),
            '& > legend': {
                width: 0,
            },
        },
    };
});

export default forwardRef(({ disabled, error, label, labelId, renderValue, required, size, ...rest }, ref) => {
    if (label && !labelId) {
        throw new Error(
            'Shell: Missing labelId in component Select. You must provide a labelId when also providing a label.'
        );
    }

    const theme = useTheme();
    const { device } = useViewport();

    const mappedSize =
        size ||
        {
            mobile: 'medium',
            tablet: 'medium',
            desktop: 'medium',
        }[device];

    const fontStyles = {
        small: {
            minHeight: '1.5rem',
            fontSize: `${theme.typography.p3.fontSize.small}rem`,
            fontWeight: theme.typography.p3.fontWeight.regular,
            letterSpacing: `${theme.typography.p3.letterSpacing.small}rem`,
        },
        medium: {
            minHeight: '1.625rem',
            fontSize: `${theme.typography.p2.fontSize.large}rem`,
            fontWeight: theme.typography.p2.fontWeight.regular,
            letterSpacing: `${theme.typography.p2.letterSpacing.large}rem`,
        },
        large: {
            minHeight: '1.75rem',
            fontSize: `${theme.typography.p1.fontSize.large}rem`,
            fontWeight: theme.typography.p1.fontWeight.regular,
            letterSpacing: `${theme.typography.p1.letterSpacing.large}rem`,
        },
    }[mappedSize];

    const SelectComponent = (
        <CustomSelect
            ref={ref}
            aria-disabled={disabled}
            $disabled={disabled}
            error={error}
            fontStyles={fontStyles}
            MenuProps={{
                sx: {
                    '& > div.MuiBackdrop-root': { background: 'transparent' },
                    '& > div.MuiPaper-root': {
                        borderRadius: '8px',
                        maxHeight: '168px',
                        boxShadow: theme.palette.ensightful.shadows[1],
                        transform: 'translateY(1px) !important',
                        '& > ul.MuiList-root': {
                            maxHeight: '168px',
                            padding: theme.spacing(2),
                            overflowY: 'scroll',
                            '& > li.MuiMenuItem-root': {
                                borderRadius: '4px',
                                padding: theme.spacing(1, 2),
                                ...fontStyles,
                                '&:hover': {
                                    background: theme.palette.ensightful.primary[50],
                                },
                                '&.Mui-focusVisible': {
                                    background: theme.palette.ensightful.primary[50],
                                },
                                '&.Mui-selected': {
                                    background: theme.palette.ensightful.primary[50],
                                },
                                ...(renderValue && {
                                    height: 'fit-content',
                                    '&:not(:last-child)': {
                                        marginBottom: theme.spacing(1),
                                    },
                                }),
                            },
                        },
                    },
                },
            }}
            renderValue={renderValue}
            $size={mappedSize}
            {...rest}
        />
    );

    return label ? (
        <CustomFormControl renderValue={renderValue}>
            <CustomInputLabel id={labelId} color={'primary'} $disabled={disabled} required={required} shrink>
                {label}
            </CustomInputLabel>
            {cloneElement(SelectComponent, { label, labelId })}
        </CustomFormControl>
    ) : (
        SelectComponent
    );
});
